import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { ArrowLeft, ArrowRight, Save, X, Edit3 } from "lucide-react";
import RenderReviewDialog from "../RenderReviewDialog";
import PrologueBanner from "../PrologueBanner";
import EpilogueBanner from "../EpilogueBanner";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import { toast } from "sonner";
import { updateExampleInBackend } from "../../lib/exampleStorage";

/**
 * Dialog to preview/edit an example with full activity context
 * Shows the complete activity rendered with example data
 * @param {Object} props - Component props
 * @param {Object} props.example - The example object to preview/edit
 * @param {string} props.activityId - The activity/prescription ID
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {boolean} [props.isEditMode=false] - If true, allows editing the example
 * @param {function} [props.onSaveComplete] - Callback when save is successful
 */
const ExamplePreviewDialog = ({
  example,
  activityId,
  open,
  onOpenChange,
  isEditMode = false,
  onSaveComplete,
  marketplaceEntityId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const [logEntries, setLogEntries] = useState([]);
  // Step index: 0 = Prologue, 1..N = Activity panes, N+1 = Epilogue
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processNotes, setProcessNotes] = useState({});
  const [prologue, setPrologue] = useState("");
  const [epilogue, setEpilogue] = useState("");
  const [prologueRecordings, setPrologueRecordings] = useState(null);
  const [epilogueRecordings, setEpilogueRecordings] = useState(null);

  // Edit mode state
  const [exampleName, setExampleName] = useState("");
  const [exampleDescription, setExampleDescription] = useState("");
  const [editedLogEntries, setEditedLogEntries] = useState([]);

  const renderReviewRef = useRef(null);

  // Use edited log entries in edit mode, otherwise use original
  const activeLogEntries = isEditMode ? editedLogEntries : logEntries;

  // Check if prologue/epilogue have real content (no placeholder when empty)
  const hasPrologueContent =
    (prologue && prologue.replace(/<[^>]*>/g, "").trim().length > 0) ||
    prologueRecordings?.audio ||
    prologueRecordings?.video ||
    prologueRecordings?.image;
  const hasEpilogueContent =
    (epilogue && epilogue.replace(/<[^>]*>/g, "").trim().length > 0) ||
    epilogueRecordings?.audio ||
    epilogueRecordings?.video ||
    epilogueRecordings?.image;

  // Review steps: Prologue? + Activity panes + Epilogue? (prologue/epilogue only when they have content)
  const reviewSteps = React.useMemo(() => {
    const steps = [];
    if (hasPrologueContent) steps.push({ type: "prologue" });
    activeLogEntries.forEach((_, i) =>
      steps.push({ type: "activity", activityIndex: i }),
    );
    if (hasEpilogueContent) steps.push({ type: "epilogue" });
    return steps;
  }, [activeLogEntries, hasPrologueContent, hasEpilogueContent]);

  const totalSteps = reviewSteps.length;
  const currentStep = reviewSteps[stepIndex] ?? null;
  const isPrologueStep = currentStep?.type === "prologue";
  const isEpilogueStep = currentStep?.type === "epilogue";
  const currentLogEntry =
    currentStep?.type === "activity" && currentStep.activityIndex != null
      ? activeLogEntries[currentStep.activityIndex]
      : undefined;
  const activityIndex =
    currentStep?.type === "activity" && currentStep.activityIndex != null
      ? currentStep.activityIndex
      : 0;

  // Fetch prescription when example changes
  useEffect(() => {
    if (open && activityId && example) {
      fetchPrescription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activityId, example]);

  const fetchPrescription = async () => {
    try {
      setLoading(true);

      // Use the same API as Review - /api/review/process/:processId
      // This returns processInfo + logEntries built from process_log (includes AI responses)
      const response = await retryFetch(
        `${config.API_BASE}/api/review/process/${example._id}`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch example data");
      }

      const { processInfo, logEntries: entries } = await response.json();

      // Extract notes, prologue, epilogue, and recordings from process info
      setProcessNotes(processInfo?.notes || {});
      setPrologue(processInfo?.prologue || "");
      setEpilogue(processInfo?.epilogue || "");
      setPrologueRecordings(
        processInfo?.prologueRecordings
          ? {
              ...processInfo.prologueRecordings,
              image: processInfo.prologueImage,
            }
          : null,
      );
      setEpilogueRecordings(
        processInfo?.epilogueRecordings
          ? {
              ...processInfo.epilogueRecordings,
              image: processInfo.epilogueImage,
            }
          : null,
      );

      // Use logEntries directly from review API (same as Review.tsx)
      // Filter out system markers (same as runActivity's handleFollowExample)
      const cleanedEntries = (entries || []).filter(
        (entry) =>
          !entry.activity?.startsWith("TERMINATE") &&
          !entry.activity?.startsWith("REOPEN|") &&
          !entry.activity?.startsWith("CURRENT_SCREEN|") &&
          !entry.isCurrentScreen,
      );

      setLogEntries(cleanedEntries);
      setEditedLogEntries(JSON.parse(JSON.stringify(cleanedEntries))); // Deep clone for editing
      setStepIndex(0);

      // Initialize edit mode fields
      if (isEditMode) {
        setExampleName(processInfo?.exampleName || example.name || "");
        setExampleDescription(
          processInfo?.exampleDescription || example.description || "",
        );
      }
    } catch (error) {
      console.error("Error fetching prescription:", error);
      toast.error(t("failed_to_load_activity") || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setLogEntries([]);
      setStepIndex(0);
      setProcessNotes({});
      setPrologue("");
      setEpilogue("");
      setPrologueRecordings(null);
      setEpilogueRecordings(null);
    }
  }, [open]);

  // Clamp stepIndex when totalSteps changes (e.g. prologue/epilogue hidden when empty)
  useEffect(() => {
    if (stepIndex >= totalSteps && totalSteps > 0) {
      setStepIndex(Math.max(0, totalSteps - 1));
    }
  }, [totalSteps, stepIndex]);

  // Navigation handlers (raw — used internally and passed to RenderReviewDialog)
  const handlePrevious = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const handleNext = () => {
    if (stepIndex < totalSteps - 1) setStepIndex(stepIndex + 1);
  };

  // External button clicks route through RenderReviewDialog's Mode 3 interceptor when on an activity pane
  const handleExternalPrevious = () => {
    const intercepted = renderReviewRef.current?.interceptPrevious;
    intercepted ? intercepted() : handlePrevious();
  };

  const handleExternalNext = () => {
    const intercepted = renderReviewRef.current?.interceptNext;
    intercepted ? intercepted() : handleNext();
  };

  // Edit mode handlers
  const handleUserDataChange = (fieldKey, value) => {
    if (!isEditMode) return;

    setEditedLogEntries((prevEntries) => {
      const newEntries = [...prevEntries];
      if (newEntries[activityIndex]) {
        newEntries[activityIndex] = {
          ...newEntries[activityIndex],
          userData: {
            ...newEntries[activityIndex].userData,
            [fieldKey]: value,
          },
        };
      }
      return newEntries;
    });
  };

  const handlePrologueChange = (value) => {
    if (!isEditMode) return;
    setPrologue(value);
  };

  const handleEpilogueChange = (value) => {
    if (!isEditMode) return;
    setEpilogue(value);
  };

  // Save handler
  const handleSave = async () => {
    if (!example || !isEditMode) return;

    try {
      setSaving(true);

      // Validate required fields
      if (!exampleName.trim()) {
        toast.error(t("example_name_required") || "Example name is required");
        return;
      }

      // Build stepStack from edited log entries
      const stepStack = editedLogEntries.map((entry) => ({
        activity: entry.activity,
        userData: entry.userData || {},
        selectedOptionId: entry.selectedOptionId || null,
      }));

      // Call the update API with stepStack
      await updateExampleInBackend(
        example._id,
        exampleName,
        exampleDescription,
        prologue,
        epilogue,
        processNotes,
        stepStack,
        retryFetch,
      );

      toast.success(
        t("example_updated_successfully") || "Example updated successfully",
      );

      // Call onSaveComplete callback
      if (onSaveComplete) {
        onSaveComplete();
      }

      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving example:", error);
      toast.error(t("failed_to_save_example") || "Failed to save example");
    } finally {
      setSaving(false);
    }
  };

  if (!example) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-full max-h-[100vh] flex-col max-w-full gap-0 overflow-hidden p-0 top-4 translate-y-0 sm:top-4"
        hideClose={isEditMode}
      >
        <TooltipProvider>
          <DialogHeader className="flex-shrink-0 px-4 py-3 border-b space-y-0">
            {/* Edit Mode Banner with Action Buttons */}
            {isEditMode && (
              <Alert className="mb-3 border-primary/20 bg-primary/5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center flex-1 gap-2">
                    <Edit3 className="flex-shrink-0 w-4 h-4 text-primary" />
                    <AlertDescription className="mb-0 text-sm">
                      <span className="font-semibold">
                        {t("edit_mode") || "Edit Mode"}:
                      </span>{" "}
                      {t("edit_mode_preview_instruction") ||
                        "Review each step and make necessary changes. Click 'Save' when done."}
                    </AlertDescription>
                  </div>
                  <div className="flex items-center flex-shrink-0 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenChange(false)}
                      disabled={saving}
                      className="h-8 px-3"
                    >
                      <X className="h-3.5 w-3.5 mr-1.5" />
                      {t("cancel")}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={saving || !exampleName.trim()}
                      className="h-8 px-3"
                    >
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                      {saving
                        ? t("saving") || "Saving..."
                        : t("save") || "Save"}
                    </Button>
                  </div>
                </div>
              </Alert>
            )}

            <div className="mb-2">
              {isEditMode ? (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="example-name"
                      className="text-sm font-medium"
                    >
                      {t("example_name") || "Example Name"}{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="example-name"
                      value={exampleName}
                      onChange={(e) => setExampleName(e.target.value)}
                      placeholder={
                        t("enter_example_name") || "Enter example name"
                      }
                      className="text-base"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="example-description"
                      className="text-sm font-medium"
                    >
                      {t("description") || "Description"}{" "}
                      <span className="text-xs text-gray-500">
                        {t("optional")}
                      </span>
                    </Label>
                    <Textarea
                      id="example-description"
                      value={exampleDescription}
                      onChange={(e) => setExampleDescription(e.target.value)}
                      placeholder={
                        t("example_description_guidance_placeholder") ||
                        "Enter description (optional)"
                      }
                      rows={2}
                      className="text-sm resize-none"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <DialogTitle className="text-lg font-semibold">
                    {example.name}
                  </DialogTitle>
                  <p className="mt-1 text-sm text-gray-500">
                    {t("example_preview_description")}
                  </p>
                  {marketplaceEntityId && (
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-1 h-auto p-0 text-sm font-normal text-primary"
                      onClick={() =>
                        navigate(`/marketplace/product/${marketplaceEntityId}`)
                      }
                    >
                      {t("view_in_marketplace") ||
                        "View Activity in Marketplace"}
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* Navigation: Prev, count, Next grouped together like Review */}
            <div className="flex items-center justify-center pt-1">
              <div className="inline-flex items-center gap-2 rounded-md border bg-muted/30 px-1 py-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExternalPrevious}
                  disabled={stepIndex === 0}
                  className="h-7 px-2 gap-1"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  <span className="text-xs">{t("previous")}</span>
                </Button>
                <span className="text-sm font-medium text-muted-foreground min-w-[3.5rem] text-center tabular-nums">
                  {currentLogEntry ? (
                    `${activityIndex + 1} / ${activeLogEntries.length}`
                  ) : isPrologueStep ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-primary cursor-help inline">
                          {t("prologue") || "Prologue"}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        {t("tooltip_prologue")}
                      </TooltipContent>
                    </Tooltip>
                  ) : isEpilogueStep ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-primary cursor-help inline">
                          {t("epilogue") || "Epilogue"}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        {t("tooltip_epilogue")}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExternalNext}
                  disabled={stepIndex === totalSteps - 1}
                  className="h-7 px-2 gap-1"
                >
                  <span className="text-xs">{t("next")}</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center">
                  <div className="w-8 h-8 mx-auto mb-4 border-4 border-primary rounded-full border-t-transparent animate-spin"></div>
                  <p className="text-sm text-gray-500">
                    {t("loading") || "Loading..."}
                  </p>
                </div>
              </div>
            ) : isPrologueStep ? (
              <div className="flex-1 overflow-auto min-h-0 w-full">
                <PrologueBanner
                  prologue={prologue}
                  isEditMode={isEditMode}
                  onChange={isEditMode ? handlePrologueChange : undefined}
                  recordings={prologueRecordings}
                  alwaysShowAsPane
                />
              </div>
            ) : isEpilogueStep ? (
              <div className="flex-1 overflow-auto min-h-0 w-full">
                <EpilogueBanner
                  epilogue={epilogue}
                  isEditMode={isEditMode}
                  onChange={isEditMode ? handleEpilogueChange : undefined}
                  recordings={epilogueRecordings}
                  alwaysShowAsPane
                />
              </div>
            ) : currentLogEntry ? (
              <div className="flex-1 overflow-auto">
                <RenderReviewDialog
                  logEntry={currentLogEntry}
                  onQuotePane={null}
                  notes={processNotes}
                  processId={example?._id}
                  isProcessOwner={isEditMode}
                  prologue={prologue}
                  epilogue={epilogue}
                  isEditMode={isEditMode}
                  onPrologueChange={
                    isEditMode ? handlePrologueChange : undefined
                  }
                  onEpilogueChange={
                    isEditMode ? handleEpilogueChange : undefined
                  }
                  onUserDataChange={
                    isEditMode ? handleUserDataChange : undefined
                  }
                  prologueRecordings={prologueRecordings || {}}
                  epilogueRecordings={epilogueRecordings || {}}
                  onNotesChange={isEditMode ? setProcessNotes : undefined}
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  ref={renderReviewRef}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center flex-1">
                <p className="text-sm text-gray-500">{t("no_data")}</p>
              </div>
            )}
          </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
};

export default ExamplePreviewDialog;
